import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"
import { ICON_SIZE, resolveColor } from "../lib/style-resolver"
import { resolveStyleTokenValue } from "../lib/style-token-resolver"
import {
  hasSourceVectorPayload,
  renderSourceVectorPayload,
  sourceFillBoxStyle,
} from "./source-derived-rendering"

export function IconRenderer({ element }: RendererProps) {
  const { resolveSymbol, designTokens } = useRenderContext()
  const atomDefaults = useAtomDefaults("icon")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const explicitSize = (element as { size?: unknown }).size
  const resolvedSize = explicitSize != null
    ? String(explicitSize)
    : atomDefaults.defaultSize ?? "default"
  const rawSize = resolveStyleTokenValue(explicitSize, designTokens, "width")
  const resolvedIconSize = resolveIconSize(rawSize)

  const rawName = element.name ?? element.icon ?? ""
  const name = rawName.includes("#{")
    ? String(resolveSymbol(rawName) ?? "")
    : rawName

  const IconComponent = resolveIcon(name)
  if (!IconComponent && hasSourceVectorPayload(element)) {
    return (
      <span
        className={cn(element.className)}
        style={{
          ...sourceFillBoxStyle(element, designTokens),
          ...resolvedIconSize.style,
          position: "relative",
          display: "block",
        }}
        data-testid={`icon-${element.id}`}
        data-atom-type="icon"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        data-cogo-source-vector-icon="true"
      >
        {renderSourceVectorPayload(element, "cogo-source-icon-vector")}
      </span>
    )
  }
  if (!IconComponent) return null

  // Resolve color via centralized resolver
  const rawColor = element.color as string | undefined
  const colorSource = rawColor?.includes("#{")
    ? resolveSymbol(rawColor)
    : rawColor
  const resolvedColor = resolveStyleTokenValue(colorSource, designTokens, "color")
  const { className: colorClass, style: colorStyle } = resolveColor(resolvedColor ? String(resolvedColor) : undefined)

  // Resolve size via centralized tokens. Supplying the native Lucide `size`
  // keeps the SVG width/height attributes aligned with the authored property;
  // inline CSS remains authoritative over Lucide's default 24px attributes.
  const inlineStyle: React.CSSProperties = { ...colorStyle, ...resolvedIconSize.style }

  const hasStyle = Object.keys(inlineStyle).length > 0
  const shouldFill = element.fill === true

  return (
    <IconComponent
      className={cn(resolvedIconSize.className, colorClass, element.className)}
      style={hasStyle ? inlineStyle : undefined}
      size={resolvedIconSize.nativeSize}
      fill={shouldFill ? "currentColor" : "none"}
      data-testid={`icon-${name}`}
      data-atom-type="icon"
      data-icon={name}
      data-name={name}
      data-color={colorSource == null ? undefined : String(colorSource)}
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    />
  )
}

function resolveIconSize(value: unknown): {
  className: string
  nativeSize: number | undefined
  style: React.CSSProperties
} {
  if (typeof value === "number" && Number.isFinite(value)) {
    return {
      className: "",
      nativeSize: value,
      style: { width: value, height: value },
    }
  }
  if (typeof value === "string" && /^\d+(?:\.\d+)?px$/.test(value)) {
    return {
      className: "",
      nativeSize: Number.parseFloat(value),
      style: { width: value, height: value },
    }
  }
  const sizeKey = typeof value === "string" ? value : "default"
  return {
    className: ICON_SIZE[sizeKey] ?? ICON_SIZE.default,
    nativeSize: undefined,
    style: {},
  }
}
